import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, IndianRupee } from "lucide-react";
import { toast } from "react-toastify";
import Layout from "../../../components/layout";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { getLoggingUser } from "../../../features/loggingUser/loggingUserSlice";
import { getSelectedBranch } from "../../../features/settings/settingsSlice";
import { useCanAccess } from "../../../hooks/useCanAccess";
import {
  fetchPartyRates,
  updatePartyRates,
  getRatePartyInfo,
  getProcessRates,
  getProcessRatesLoading,
  getProcessRatesSaving,
  resetProcessRates,
} from "../../../features/parties/processRatesSlice";
import { toastStyles } from "../../../static/styles";
import { t } from "../../../locales";
import { route } from "../../../static/route";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import ProcessRateList from "../../../components/parties/ProcessRateList";
import { ProcessRateRow } from "../../../types/processRate";

const PartyRatesPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const canAccess = useCanAccess();
  const loggingUser = useAppSelector(getLoggingUser);
  const selectedBranch = useAppSelector(getSelectedBranch);
  const partyInfo = useAppSelector(getRatePartyInfo);
  const rates = useAppSelector(getProcessRates);
  const loading = useAppSelector(getProcessRatesLoading);
  const saving = useAppSelector(getProcessRatesSaving);

  useEffect(() => {
    if (loggingUser?.permissions && selectedBranch && !canAccess("parties.rate")) {
      navigate(route.dashboard);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggingUser, selectedBranch]);

  useEffect(() => {
    if (id) dispatch(fetchPartyRates(Number(id)));
    return () => {
      dispatch(resetProcessRates());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSaveTab = async (processDefinitionId: number, rows: ProcessRateRow[]) => {
    if (!id) return;
    const response: any = await dispatch(
      updatePartyRates({ partyId: Number(id), process_definition_id: processDefinitionId, rates: rows })
    );
    if (response.payload?.data?.status === "success") {
      toast.success(response.payload.data.message, { ...(toastStyles as any) });
      dispatch(fetchPartyRates(Number(id)));
    } else {
      toast.error(
        response.payload?.data?.message || t.common.somethingWentWrong,
        { ...(toastStyles as any) }
      );
    }
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-4">
        <Card className="shadow-lg border-0">
          <CardContent className="p-4 md:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-[#113F67]/10 flex items-center justify-center flex-shrink-0">
                  <IndianRupee className="h-5 w-5 text-[#113F67]" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[#113F67]">{t.processRates.pageTitle}</h2>
                  <p className="text-sm text-gray-500">{partyInfo?.firm_name || "-"}</p>
                </div>
              </div>
              <Button
                variant="outline"
                className="h-10 px-4 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67] text-sm"
                onClick={() => navigate(id ? route.partiesView.replace(":id", id) : route.parties)}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t.common.back}
              </Button>
            </div>

            {loading ? (
              <div className="py-12 text-center text-gray-500">{t.common.loading}</div>
            ) : (
              <ProcessRateList
                processDefinitions={partyInfo?.process_definitions || []}
                rates={rates}
                onSaveTab={handleSaveTab}
                saving={saving}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default PartyRatesPage;
